import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import unified from 'unified';

import theme from '../assets/theme';
import { CuripodDecendant, CuripodText } from '../components/SlideComponents/slateUtil';
import remarkSlatePlugin from './remarkSlatePlugin/remarkSlate';

// Enhanced parseMarkdown function that supports bold, italic, underline and nested combinations
export const parseMarkdownEnhanced = (text: string): CuripodDecendant[] => {
  // Updated regex to handle nested formatting patterns
  const regex = /(\*\*\*([^*]*?)\*\*\*)|(\*\*_([^_]*?)_\*\*)|(_\*\*([^*]*?)\*\*_)|(\*\*([^*]*?)\*\*)|(__\*([^*]*?)\*__)|(\*__([^_]*?)__\*)|(\*([^*]*?)\*)|(__([^_]*?)__)/g;
  let match;
  const result: CuripodText[] = [];
  let lastIndex = 0;

  // Loop through all matches of various formatting combinations
  while ((match = regex.exec(text)) !== null) {
    // Add text before the markdown part
    if (match.index > lastIndex) {
      result.push({
        text: text.slice(lastIndex, match.index),
        color: theme.colors.black,
      });
    }

    let matchedText = '';
    let isBold = false;
    let isItalic = false;
    let isUnderline = false;

    // Determine which group was matched and set appropriate flags
    if (match[1]) {
      // Matched ***italic+bold***
      matchedText = match[2];
      isBold = true;
      isItalic = true;
    } else if (match[3]) {
      // Matched **_bold+underline_**
      matchedText = match[4];
      isBold = true;
      isUnderline = true;
    } else if (match[5]) {
      // Matched _**underline+bold**_
      matchedText = match[6];
      isBold = true;
      isUnderline = true;
    } else if (match[7]) {
      // Matched **bold**
      matchedText = match[8];
      isBold = true;
    } else if (match[9]) {
      // Matched __*underline+italic*__
      matchedText = match[10];
      isItalic = true;
      isUnderline = true;
    } else if (match[11]) {
      // Matched *__italic+underline__*
      matchedText = match[12];
      isItalic = true;
      isUnderline = true;
    } else if (match[13]) {
      // Matched *italic*
      matchedText = match[14];
      isItalic = true;
    } else if (match[15]) {
      // Matched __underline__
      matchedText = match[16];
      isUnderline = true;
    }

    // Add the styled part
    result.push({
      text: matchedText,
      bold: isBold || undefined,
      italic: isItalic || undefined,
      underline: isUnderline || undefined,
      color: theme.colors.black,
    });

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last styled section
  if (lastIndex < text.length) {
    result.push({ text: text.slice(lastIndex), color: theme.colors.black });
  }

  // Prevent an empty children in the array. As slate crashes if children is empty
  if (result.length === 0) {
    result.push({
      text: '',
      color: theme.colors.black,
    });
  }

  return result;
};

export const parseMarkdownToSlateV2 = async (
  markdown: string,
): Promise<CuripodDecendant[]> => {
  const virtualFile = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkSlatePlugin, {
      defaults: {
        fontSize: 14,
        color: theme.colors.black,
        align: 'left',
      },
    })
    .process(markdown);

  return virtualFile.result as CuripodDecendant[];
};

/**
 * Synchronous version of parseMarkdownToSlateV2 that supports custom initial state.
 * Parses markdown (including math $...$ and $$...$$) to Slate elements.
 */
// Todo: Check if parseMarkdownToSlate can be refactored to only use the synchronous version
export const parseMarkdownToSlateSync = (
  markdown: string,
  initialState?: { align?: string; color?: string; size?: number },
): CuripodDecendant[] => {
  const virtualFile = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkSlatePlugin, {
      defaults: {
        fontSize: initialState?.size ?? 14,
        color: initialState?.color ?? theme.colors.black,
        align: (initialState?.align as 'left' | 'right' | 'center' | undefined) ?? 'left',
      },
    })
    .processSync(markdown);

  return virtualFile.result as CuripodDecendant[];
};
